import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
  Button,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  PlayArrow,
  CloudUpload,
  CheckCircle,
  Cancel,
  Error as ErrorIcon,
  Edit,
  Save,
  Cancel as CancelIcon,
  Security,
  PlayCircleOutline
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Editor from '@monaco-editor/react';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import { AuthContext } from '../../contexts/AuthContext';
import axios from '../../api/axios';

function CodeComponent({ example, exampleId: exampleIdProp, embedded = false, splitLayout = false, onAnswerSubmitted }) {
  const params = useParams();
  const { user } = useContext(AuthContext);
  // 支持内嵌场景（如复习页）：无路由时用 example.id
  const exampleId = exampleIdProp ?? params.exampleId ?? (example?.id != null ? String(example.id) : null);

  const [code, setCode] = useState('');
  const [running, setRunning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [executeResult, setExecuteResult] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Description, 1: Code, 2: Output, 3: Test Result, 4: Submissions
  const [testTab, setTestTab] = useState(0); // 0: Public Test, 1: Secret Test
  const [editingPublicTest, setEditingPublicTest] = useState(false);
  const [editingSecretTest, setEditingSecretTest] = useState(false);
  const [publicTestCode, setPublicTestCode] = useState('');
  const [secretTestCode, setSecretTestCode] = useState('');
  const [savingTest, setSavingTest] = useState(false);

  const isNewFormat = example.publicTests || example.secretTests;
  const questionId = exampleId ? `notebook_${exampleId}` : null;

  // Fetch submissions
  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`/submissions/question/${questionId}`);
      setSubmissions(response.data);
      
      // Use latest submission code if available and code is not already set
      if (response.data.length > 0 && response.data[0].code) {
        // Check if code is JSON (for other question types) or actual code
        let isCode = true;
        try {
          const parsed = JSON.parse(response.data[0].code);
          // If it's an object, it's not code, don't use it
          if (typeof parsed === 'object' && !Array.isArray(parsed)) {
            isCode = false;
          }
        } catch (e) {
          // It's not JSON, use it as code
          isCode = true;
        }
        
        if (isCode) {
          return response.data[0].code;
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching submissions:', error);
      return null;
    }
  };

  // Initialize code from example
  useEffect(() => {
    const initCode = async () => {
      // Try to get latest submission code first
      const submissionCode = await fetchSubmissions();
      
      // If no submission, use template from codeAnswer or extract from question
      if (submissionCode) {
        setCode(submissionCode);
      } else {
        if (example.codeAnswer) {
          const cleanCode = example.codeAnswer.replace(/```python\n?/g, '').replace(/```/g, '').trim();
          setCode(cleanCode);
        } else if (example.question) {
      // Try to extract code from question
      const codeMatch = example.question.match(/```python\n?([\s\S]*?)```/);
      if (codeMatch) {
        setCode(codeMatch[1].trim());
          }
        }
      }
    };

    initCode();

    // Initialize test code
    setPublicTestCode(example.publicTests || '');
    setSecretTestCode(example.secretTests || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [example, exampleId]);


  const handleExecute = async () => {
    if (!code.trim()) {
      alert('请先编写代码');
      return;
    }

    setExecuting(true);
    setExecuteResult(null);

    try {
      const blob = new Blob([code], { type: 'text/x-python' });
      const file = new File([blob], 'solution.py', { type: 'text/x-python' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionId', questionId);

      const response = await axios.post('/submissions/execute', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setExecuteResult(response.data);
      setRunResult(null);
      setActiveTab(splitLayout ? 1 : 2); // Switch to Output tab
    } catch (error) {
      console.error('Error executing code:', error);
      setExecuteResult({
        status: 'error',
        errorMessage: error.response?.data?.error || '执行失败，请重试',
        output: ''
      });
    } finally {
      // Execution completed
    }
  };

  const handleRun = async (testType = 'public') => {
    if (!code.trim()) {
      alert('请先编写代码');
      return;
    }

    setRunning(true);
    setRunResult(null);

    try {
      const blob = new Blob([code], { type: 'text/x-python' });
      const file = new File([blob], 'solution.py', { type: 'text/x-python' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionId', questionId);
      formData.append('testType', testType);

      const response = await axios.post('/submissions/run', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setRunResult(response.data);
      setExecuteResult(null);
      setActiveTab(splitLayout ? 2 : 3); // Switch to Test Result tab
    } catch (error) {
      console.error('Error running code:', error);
      setRunResult({
        status: 'error',
        errorMessage: error.response?.data?.error || '运行失败，请重试',
        testResults: { attempted: 0, failed: 0, passed: 0 }
      });
    } finally {
      setRunning(false);
    }
  };

  const handleSubmit = async () => {
    if (!code.trim()) {
      alert('请先编写代码');
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const blob = new Blob([code], { type: 'text/x-python' });
      const file = new File([blob], 'solution.py', { type: 'text/x-python' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionId', questionId);

      const response = await axios.post('/submissions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const submission = response.data.submission;
      setResult(submission);
      await fetchSubmissions();
      setActiveTab(splitLayout ? 3 : 4); // Switch to Submissions tab
      if (onAnswerSubmitted && embedded) {
        let extra = '';
        if (submission.errorMessage) extra += `错误信息：${submission.errorMessage}`;
        if (submission.testResults) extra += (extra ? '\n' : '') + `测试结果：${JSON.stringify(submission.testResults)}`;
        onAnswerSubmitted(code, {
          isCorrect: submission.status === 'passed',
          score: submission.score,
        }, extra);
      }
    } catch (error) {
      console.error('Error submitting:', error);
      setResult({
        status: 'error',
        errorMessage: error.response?.data?.error || '提交失败，请重试'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePublicTest = async () => {
    if (user?.role !== 'teacher') return;
    
    setSavingTest(true);
    try {
      await axios.put(`/notebook-examples/${exampleId}`, {
        publicTests: publicTestCode
      });
      // Update local example state
      example.publicTests = publicTestCode;
      setEditingPublicTest(false);
      alert('公开测试用例已保存');
    } catch (error) {
      console.error('Error saving public test:', error);
      alert('保存失败：' + (error.response?.data?.error || '请重试'));
    } finally {
      setSavingTest(false);
      }
  };

  const handleSaveSecretTest = async () => {
    if (user?.role !== 'teacher') return;
    
    setSavingTest(true);
    try {
      await axios.put(`/notebook-examples/${exampleId}`, {
        secretTests: secretTestCode
      });
      // Update local example state
      example.secretTests = secretTestCode;
      setEditingSecretTest(false);
      alert('秘密测试用例已保存');
    } catch (error) {
      console.error('Error saving secret test:', error);
      alert('保存失败：' + (error.response?.data?.error || '请重试'));
    } finally {
      setSavingTest(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return 'success';
      case 'failed': return 'error';
      case 'error': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <CheckCircle fontSize="small" />;
      case 'failed': return <Cancel fontSize="small" />;
      case 'error': return <ErrorIcon fontSize="small" />;
      default: return null;
    }
  };
  
  const CodeBlock = ({ code: codeContent, language = 'python' }) => {
    const cleanCode = codeContent.replace(/```python\n?/g, '').replace(/```/g, '').trim();
    return (
      <Box sx={{ my: 2 }}>
        <SyntaxHighlighter language={language} style={vscDarkPlus}>
          {cleanCode}
        </SyntaxHighlighter>
      </Box>
    );
  };
  
  return (
    <Box sx={{ width: '100%', maxWidth: splitLayout ? '100%' : '1200px', mx: splitLayout ? 0 : 'auto' }}>
      <Paper elevation={embedded ? 0 : undefined} sx={{ p: splitLayout ? 1.5 : 3, mb: splitLayout ? 0 : 2, ...(embedded && { bgcolor: 'transparent', pl: 0, pr: 0 }) }}>
      {!splitLayout && (
        <Box sx={{ mb: 3 }}>
          <MarkdownRenderer content={example.question} />
        </Box>
      )}
      
        {/* Action Buttons */}
        <Stack direction="row" spacing={2} sx={{ mb: splitLayout ? 1.5 : 3 }} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={handleExecute}
            disabled={executing}
          >
            {executing ? '执行中...' : 'Run'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CloudUpload />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? '提交中...' : 'Submit'}
          </Button>
          {splitLayout && example.publicTests && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayCircleOutline />}
              onClick={() => handleRun('public')}
              disabled={running}
            >
              {running ? '运行中...' : '运行公开测试'}
            </Button>
          )}
        </Stack>

        {/* Main Tabs */}
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: splitLayout ? 0 : 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            {!splitLayout && <Tab label="题目描述" />}
            <Tab label="代码" />
            <Tab label="输出" />
            <Tab label="测试结果" />
            <Tab label="提交记录" />
          </Tabs>

          {/* Description Tab */}
          {!splitLayout && activeTab === 0 && (
            <Box sx={{ p: 2, minHeight: 200, overflow: 'auto' }}>
              {example.question ? (
                <MarkdownRenderer content={example.question} />
              ) : (
                <Typography color="text.secondary">暂无题目描述。</Typography>
              )}
            </Box>
          )}

          {/* Code Tab */}
          {(splitLayout ? activeTab === 0 : activeTab === 1) && (
            <Box sx={{ p: splitLayout ? 1 : 2, ...(splitLayout && { overflow: 'auto' }) }}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: splitLayout ? 0 : 2 }}>
            <Editor
              height={splitLayout ? '540px' : '400px'}
              language="python"
              value={code}
              onChange={(value) => setCode(value || '')}
              theme="light"
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                lineNumbersMinChars: 3,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 4,
                wordWrap: 'off',
                fontFamily: "'Monaco', 'Menlo', 'Consolas', 'Courier New', monospace",
                    renderWhitespace: 'selection',
                    bracketPairColorization: { enabled: true },
              }}
            />
          </Box>
          
              {/* Test Cases Section - 在 splitLayout 时移至聊天区展示，此处隐藏 */}
              {isNewFormat && !splitLayout && (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Tabs value={testTab} onChange={(e, v) => setTestTab(v)}>
              {example.publicTests && (
                <Tab label="公开测试用例" />
              )}
                    {example.secretTests && user?.role === 'teacher' && (
                      <Tab label="秘密测试用例" icon={<Security fontSize="small" />} iconPosition="start" />
              )}
            </Tabs>
            
                  {/* Public Tests */}
            {example.publicTests && (
                    <Box sx={{ p: 2, display: testTab === 0 ? 'block' : 'none' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          公开测试用例
                        </Typography>
                        {user?.role === 'teacher' && (
                          <Stack direction="row" spacing={1}>
                            {!editingPublicTest ? (
                              <Button
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => setEditingPublicTest(true)}
                              >
                                编辑
                              </Button>
                            ) : (
                              <>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<Save />}
                                  onClick={handleSavePublicTest}
                                  disabled={savingTest}
                                >
                                  保存
                                </Button>
                                <Button
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  onClick={() => {
                                    setEditingPublicTest(false);
                                    setPublicTestCode(example.publicTests || '');
                                  }}
                                  disabled={savingTest}
                                >
                                  取消
                                </Button>
                              </>
                            )}
                          </Stack>
                        )}
                      </Stack>
                      {editingPublicTest && user?.role === 'teacher' ? (
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                          <Editor
                            height="200px"
                            language="python"
                            value={publicTestCode}
                            onChange={(value) => setPublicTestCode(value || '')}
                            theme="light"
                            options={{
                              minimap: { enabled: false },
                              fontSize: 13,
                              lineNumbers: 'on',
                            }}
                          />
                        </Box>
                      ) : (
                        <>
                <CodeBlock code={example.publicTests} language="python" />
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PlayCircleOutline />}
                            onClick={() => handleRun('public')}
                            disabled={running}
                            sx={{ mt: 2 }}
                          >
                            {running ? '运行中...' : '运行公开测试'}
                          </Button>
                        </>
                      )}
              </Box>
            )}
            
                  {/* Secret Tests (Teacher Only) */}
                  {example.secretTests && user?.role === 'teacher' && (
                    <Box sx={{ p: 2, display: testTab === (example.publicTests ? 1 : 0) ? 'block' : 'none', bgcolor: '#fff3cd' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          秘密测试用例
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {!editingSecretTest ? (
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => setEditingSecretTest(true)}
                            >
                              编辑
                            </Button>
                          ) : (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<Save />}
                                onClick={handleSaveSecretTest}
                                disabled={savingTest}
                              >
                                保存
                              </Button>
                              <Button
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={() => {
                                  setEditingSecretTest(false);
                                  setSecretTestCode(example.secretTests || '');
                                }}
                                disabled={savingTest}
                              >
                                取消
                              </Button>
                            </>
                          )}
                        </Stack>
                      </Stack>
                      {editingSecretTest ? (
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                          <Editor
                            height="200px"
                            language="python"
                            value={secretTestCode}
                            onChange={(value) => setSecretTestCode(value || '')}
                            theme="light"
                            options={{
                              minimap: { enabled: false },
                              fontSize: 13,
                              lineNumbers: 'on',
                            }}
                          />
                        </Box>
                      ) : (
                        <>
                <CodeBlock code={example.secretTests} language="python" />
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            startIcon={<Security />}
                            onClick={() => handleRun('secret')}
                            disabled={running}
                            sx={{ mt: 2 }}
                          >
                            {running ? '运行中...' : '运行秘密测试'}
                          </Button>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Output Tab */}
          {(splitLayout ? activeTab === 1 : activeTab === 2) && (
            <Box sx={{ p: splitLayout ? 1 : 2, ...(splitLayout && { overflow: 'auto' }) }}>
              {executeResult ? (
                <Box>
                  <Alert
                    severity={executeResult.status === 'success' ? 'success' : 'error'}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {executeResult.status === 'success' ? '✅ 代码执行成功' : '❌ 执行错误'}
                    </Typography>
                  </Alert>

                  {executeResult.output && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        输出:
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'grey.100', fontFamily: 'monospace', fontSize: '12px', overflow: 'auto', maxHeight: 400 }}>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                          {executeResult.output}
                        </pre>
                      </Paper>
                    </Box>
                  )}

                  {executeResult.errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {executeResult.errorMessage}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.disabled" gutterBottom>
                    还没有输出
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    点击 "Run" 按钮执行代码并查看输出
                  </Typography>
              </Box>
            )}
          </Box>
          )}

          {/* Test Result Tab */}
          {(splitLayout ? activeTab === 2 : activeTab === 3) && (
            <Box sx={{ p: splitLayout ? 1 : 2, ...(splitLayout && { overflow: 'auto' }) }}>
              {runResult ? (
                <Box>
                  <Alert
                    severity={runResult.status === 'passed' ? 'success' : runResult.status === 'error' ? 'warning' : 'error'}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {runResult.status === 'passed' ? '✅ 所有测试通过!' : 
                       runResult.status === 'error' ? '⚠️ 运行时错误' : '❌ 部分测试失败'}
                    </Typography>
                    {runResult.testResults && (
                      <Typography variant="body1" gutterBottom>
                        <strong>{runResult.testResults.passed || 0}/{runResult.testResults.attempted || 0}</strong> 个测试用例通过
                      </Typography>
                    )}
                    {runResult.score !== undefined && (
                      <Typography variant="body1">
                        得分: <strong>{runResult.score}/100</strong>
                      </Typography>
                    )}
                  </Alert>

                  {runResult.output && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        测试输出
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'grey.100', fontFamily: 'monospace', fontSize: '12px', overflow: 'auto', maxHeight: 400 }}>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                          {runResult.output.split('\n').filter(line => !line.includes('DEBUG') && !line.includes('[Python Runner]')).join('\n')}
                        </pre>
                      </Paper>
                    </Box>
                  )}

                  {runResult.errorMessage && runResult.status === 'error' && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {runResult.errorMessage}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.disabled" gutterBottom>
                    还没有测试结果
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    点击 "运行公开测试" 或 "运行秘密测试" 来测试你的代码
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Submissions Tab */}
          {(splitLayout ? activeTab === 3 : activeTab === 4) && (
            <Box sx={{ p: splitLayout ? 1 : 2, ...(splitLayout && { overflow: 'auto' }) }}>
              {submissions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.disabled" gutterBottom>
                    还没有提交记录
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    点击 "Submit" 按钮提交你的代码
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    提交历史 ({submissions.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="140"><strong>状态</strong></TableCell>
                          <TableCell align="center" width="100"><strong>得分</strong></TableCell>
                          <TableCell align="center" width="120"><strong>测试</strong></TableCell>
                          <TableCell width="150"><strong>提交时间</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {submissions.map((sub, index) => (
                          <TableRow 
                            key={sub.id} 
                            hover
                            sx={{ 
                              bgcolor: index === 0 ? 'action.hover' : 'inherit',
                              borderLeft: index === 0 ? '3px solid' : 'none',
                              borderLeftColor: sub.status === 'passed' ? 'success.main' : 'error.main'
                            }}
                          >
                            <TableCell>
                              <Chip
                                label={sub.status === 'passed' ? '通过' : '错误'}
                                color={getStatusColor(sub.status)}
                                size="small"
                                icon={getStatusIcon(sub.status)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography 
                                variant="body2" 
                                fontWeight="bold" 
                                color={sub.status === 'passed' ? 'success.main' : 'text.primary'}
                              >
                                {sub.score}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {sub.testResults && typeof sub.testResults === 'object' ? (
                                <Typography variant="body2">
                                  {sub.testResults.passed}/{sub.testResults.attempted}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(sub.submittedAt).toLocaleString('zh-CN', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {result && (
                    <Alert 
                      severity={result.status === 'passed' ? 'success' : 'error'}
                      sx={{ mt: 3 }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        最新提交
                      </Typography>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          状态: <strong>{result.status === 'passed' ? '通过 ✅' : '错误 ❌'}</strong>
                        </Typography>
                        <Typography variant="body2">
                          最终得分: <strong>{result.score}/100</strong>
                        </Typography>
                        {result.testResults && (
                          <Typography variant="body2">
                            测试结果: {result.testResults.passed}/{result.testResults.attempted} 通过 (包括秘密测试)
                          </Typography>
                        )}
                        {result.errorMessage && (
                          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                            {result.errorMessage}
                          </Typography>
                        )}
                      </Stack>
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>
      
      {example.explanation && !splitLayout && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Box component="div" variant="body2">
            <MarkdownRenderer content={example.explanation} />
          </Box>
        </Alert>
      )}
    </Box>
  );
}

export default CodeComponent;
